/**
 * ForgotPasswordPage - Page Component
 * Recuperación de contraseña en 3 pasos: email → OTP → nueva contraseña.
 * Toda la lógica vive en el hook useForgotPassword (Clean Architecture).
 * Anti-enumeración: el paso email SIEMPRE avanza a OTP.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { OtpInput } from '@/components/auth/OtpInput';
import { PasswordWithConfirm } from '@/components/auth/PasswordWithConfirm';
import { useForgotPassword } from '@/hooks/useForgotPassword';
import { useOtpCooldown } from '@/hooks/useOtpCooldown';
import { maskEmail, maskPhone, formatTime, EMAIL_OTP_LENGTH } from '@/lib/otp';
import { validateIdentifier, isEmail } from '@/lib/identifier';
import { ROUTES } from '@/constants';

export function ForgotPasswordPage() {
  const { step, identifier, error, loading, submitEmail, submitOtp, submitNewPassword, resend } =
    useForgotPassword();

  const [id, setId] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [valid, setValid] = useState(false);

  const { secondsLeft, canResend, markSent } = useOtpCooldown('forgot');

  // Al entrar al paso OTP arrancamos el cooldown del reenvío.
  useEffect(() => {
    if (step === 'otp') markSent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleEmail = (e: FormEvent) => {
    e.preventDefault();
    void submitEmail(id.trim());
  };

  const handleOtp = (e: FormEvent) => {
    e.preventDefault();
    void submitOtp(code);
  };

  const handleNewPassword = (e: FormEvent) => {
    e.preventDefault();
    void submitNewPassword(password);
  };

  const handleResend = () => {
    void resend().then(() => markSent());
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Recuperar contraseña
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {step === 'email' && 'Ingresa tu usuario o correo para recibir un código'}
            {step === 'otp' && 'Ingresa el código que te enviamos'}
            {step === 'new-password' && 'Define tu nueva contraseña'}
          </p>
        </div>

        {step === 'email' && (
          /* ── Paso 1: identificador ── */
          <form
            onSubmit={handleEmail}
            className="space-y-6 rounded-lg border border-border bg-card p-8 shadow-sm"
            noValidate
          >
            {error && (
              <div
                className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                role="alert"
              >
                {error}
              </div>
            )}

            <Input
              label="Usuario o correo"
              type="text"
              name="username"
              placeholder="Ingresa tu usuario o correo"
              value={id}
              onChange={(e) => setId(e.target.value)}
              required
              autoComplete="username"
              disabled={loading}
            />

            <Button
              type="submit"
              fullWidth
              isLoading={loading}
              disabled={loading || !validateIdentifier(id)}
            >
              Enviar código
            </Button>

            <p className="text-center text-sm">
              <Link to={ROUTES.LOGIN} className="text-primary hover:underline">
                Volver a iniciar sesión
              </Link>
            </p>
          </form>
        )}

        {step === 'otp' && (
          /* ── Paso 2: OTP ── */
          <form
            onSubmit={handleOtp}
            className="space-y-6 rounded-lg border border-border bg-card p-8 shadow-sm"
            noValidate
          >
            {error && (
              <div
                className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                role="alert"
              >
                {error}
              </div>
            )}

            <p className="text-sm text-muted-foreground">
              Te enviamos un código a{' '}
              {isEmail(identifier) ? maskEmail(identifier) : maskPhone(identifier)}
            </p>

            <OtpInput
              value={code}
              onChange={setCode}
              maxLength={EMAIL_OTP_LENGTH}
              onSubmit={() => submitOtp(code)}
              disabled={loading}
              label="Código de verificación"
            />

            <Button
              type="submit"
              fullWidth
              isLoading={loading}
              disabled={loading || code.length !== EMAIL_OTP_LENGTH}
            >
              Verificar
            </Button>

            <div className="text-center text-sm text-muted-foreground">
              {canResend ? (
                <button
                  type="button"
                  onClick={handleResend}
                  className="text-primary hover:underline"
                >
                  Reenviar código
                </button>
              ) : (
                <span>Puedes reenviar en {formatTime(secondsLeft)}</span>
              )}
            </div>
          </form>
        )}

        {step === 'new-password' && (
          /* ── Paso 3: nueva contraseña ── */
          <form
            onSubmit={handleNewPassword}
            className="space-y-6 rounded-lg border border-border bg-card p-8 shadow-sm"
            noValidate
          >
            {error && (
              <div
                className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
                role="alert"
              >
                {error}
              </div>
            )}

            <PasswordWithConfirm
              password={password}
              confirm={confirm}
              onPassword={setPassword}
              onConfirm={setConfirm}
              onValidChange={setValid}
              disabled={loading}
            />

            <Button
              type="submit"
              fullWidth
              isLoading={loading}
              disabled={loading || !valid}
            >
              Cambiar contraseña
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
